import { ChakraProvider, extendTheme, useColorMode } from "@chakra-ui/react";
import chroma from "chroma-js";
import * as React from "react";
import { defineCssVars, isColorDark } from "../utils";
import { useOkHiIncomingMessage } from "./OkHiIncomingMessageProvider";

interface Props {
  children: React.ReactNode;
}

const DEFAULT_PRIMARY_COLOR = "#005D67";
const DEFAULT_HIGHLIGHT_COLOR = "#85FFC7";

export function ThemeProvider({ children }: Props) {
  const okhiMessage = useOkHiIncomingMessage();
  const { colorMode } = useColorMode();
  const theme = React.useMemo(() => {
    const primaryColorHex =
      okhiMessage?.context?.styleSettings?.primaryColor ??
      DEFAULT_PRIMARY_COLOR;
    const secondaryColorHex =
      okhiMessage?.context?.styleSettings?.highlightColor ??
      DEFAULT_HIGHLIGHT_COLOR;
    const isPrimaryColorDark = isColorDark(primaryColorHex);
    const primaryColor = chroma(primaryColorHex);
    const secondaryColor = chroma(secondaryColorHex);
    const themeConfig = {
      colors: {
        primary: {
          500: primaryColorHex,
        },
        highlight: {
          500: secondaryColorHex,
        },
      },
      components: {
        Alert: {
          variants: {
            subtle: {
              container: {
                bg: isPrimaryColorDark
                  ? primaryColor.alpha(0.1).hex()
                  : secondaryColor.alpha(0.1).hex(),
                color: isPrimaryColorDark ? primaryColorHex : secondaryColorHex,
              },
              icon: {
                bg: isPrimaryColorDark ? primaryColor : secondaryColor,
              },
            },
          },
        },
        Button: {
          variants: {
            "okhi-solid": {
              bg: isPrimaryColorDark ? primaryColorHex : secondaryColorHex,
              color: "white",
              _hover: {
                bg: isPrimaryColorDark
                  ? primaryColor.darken(0.2).hex()
                  : secondaryColor.darken(0.2).hex(),
                _disabled: {
                  bg: isPrimaryColorDark ? primaryColorHex : secondaryColorHex,
                },
              },
              _active: {
                bg: isPrimaryColorDark
                  ? primaryColor.darken(0.3).hex()
                  : secondaryColor.darken(0.3).hex(),
              },
            },
            "okhi-white-outline": {
              bg: "white",
              color: isPrimaryColorDark ? primaryColorHex : secondaryColorHex,
              border: `1px solid ${
                isPrimaryColorDark ? primaryColorHex : secondaryColorHex
              }`,
              _hover: {
                bg: "gray.200",
                _disabled: {
                  bg: "white",
                },
              },
              _active: {
                bg: "gray.300",
              },
            },
            "okhi-dark-outline": {
              bg: "transparent",
              color: isPrimaryColorDark ? secondaryColorHex : primaryColorHex,
              border: `1px solid ${
                isPrimaryColorDark ? secondaryColorHex : primaryColorHex
              }`,
              _hover: {
                bg: isPrimaryColorDark
                  ? secondaryColor.alpha(0.12).hex()
                  : primaryColor.alpha(0.12).hex(),
                _disabled: {
                  bg: "transparent",
                },
              },
              _active: {
                bg: isPrimaryColorDark
                  ? secondaryColor.alpha(0.24).hex()
                  : primaryColor.alpha(0.24).hex(),
              },
            },
          },
        },
      },
    };
    return extendTheme(themeConfig);
  }, [okhiMessage]);
  React.useEffect(() => {
    defineCssVars(
      okhiMessage?.context.styleSettings.primaryColor ?? DEFAULT_PRIMARY_COLOR,
      okhiMessage?.context.styleSettings.highlightColor ??
        DEFAULT_HIGHLIGHT_COLOR,
      colorMode === "dark"
    );
  }, [
    okhiMessage?.context.styleSettings?.primaryColor,
    okhiMessage?.context.styleSettings?.highlightColor,
    colorMode,
  ]);
  return (
    <ChakraProvider resetCSS theme={theme}>
      {children}
    </ChakraProvider>
  );
}
